import React, { useState } from 'react';
import { ReviewData, useReviewStore } from '../../stores/reviewStore';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import RatingStars from '../common/RatingStars';
import { Button } from '../common/Button';
import ReportButtonMenu, { BottomSuccessMessage } from '../reports/ReportButtonMenu';
import ReportModal from '../reports/ReportModal';

interface ReviewListProps {
  reviews: ReviewData[];
  hasMoreReviews: boolean;
  fetchMoreReviews: () => void;
}

export const ReviewList: React.FC<ReviewListProps> = ({
  reviews,
  hasMoreReviews,
  fetchMoreReviews,
}) => {
  const { loading } = useReviewStore();

  const { t } = useTranslation('reviews');

  const [copied, setCopied] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState<ReviewData | null>(null);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDayMonthYear = (d: string | Date | null | undefined, locale = 'fr-FR') => {
    if (!d) return '';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <div>
        {reviews.length > 0 ? (
          reviews.map((review, index) => {
            const isLast = index === reviews.length - 1;
            return (
              <div
                key={`review-${index}`}
                className={`mb-2 border-b border-[#E0E0E0] py-3 ${isLast ? 'border-b-0' : ''}`}
              >
                <div className="flex items-center gap-2 justify-between">
                  <p className="text-secondary">
                    <span className="font-bold">
                      {review.reviewer.first_name} {review.reviewer.last_name}
                    </span>{' '}
                    {review.reviewer.company_name && (
                      <>
                        <span>{t('from', { ns: 'reviews' })}</span>{' '}
                        <span className="font-bold ">{review.reviewer.company_name}</span>
                      </>
                    )}
                  </p>
                  <div className="flex flex-row gap-2">
                    <div className="text-gray-400 hover:text-gray-600 text-2xl">
                      <ReportButtonMenu
                        onReport={() => {
                          setSelectedReview(review);
                          setShowReportModal(true);
                        }}
                        entityOwner={review.reviewer_id}
                        onCopySuccess={handleCopySuccess}
                        contentTypeTitle="report_this_review"
                      />
                    </div>
                    <span className="text-secondary text-sm">
                      {formatDayMonthYear(review.created_at, i18n.language)}
                    </span>
                  </div>
                </div>
                <RatingStars rating={review.rating} />
                {review.comment && <div className="mt-4">{review.comment}</div>}
              </div>
            );
          })
        ) : (
          <div className="text-center text-sm text-secondary mt-8">
            {t('noReviews', { ns: 'reviews', defaultValue: 'Aucune évaluation trouvée' })}
          </div>
        )}

        {hasMoreReviews && (
          <Button
            className="w-full mt-8 px-8 md:w-auto md:justify-self-end md:self-end md:mb-0"
            onClick={fetchMoreReviews}
            disabled={loading}
          >
            {t('loadMoreReviews', {
              ns: 'reviews',
              defaultValue: "Charger plus d'évaluations",
            })}
          </Button>
        )}
      </div>

      {showReportModal && selectedReview && (
        <ReportModal
          entityId={selectedReview.id}
          reportedId={selectedReview.reviewer_id}
          contentType="review"
          onClose={() => setShowReportModal(false)}
        />
      )}

      {copied && <BottomSuccessMessage />}
    </>
  );
};
